<template>
    <v-container>
        <v-card dark :loading="loading">
            <v-card-title class="pb-4 pt-2">
                <v-icon left color="#FF9000">mdi-warehouse</v-icon>
                {{ company?.name || 'Unternehmen' }}
            </v-card-title>
            <v-divider/>
            <v-card-text>
                <div v-if="loading" class="py-4">
                    Daten werden geladen...
                </div>

                <v-alert v-else-if="error" type="error" dense outlined>
                    {{ error }}
                </v-alert>

                <v-row v-else-if="company">
                    <v-col cols="12" md="6">
                        <div class="section-card company-meta">
                            <div><b>Art:</b> {{ companyTypeLabel }}</div>
                            <div><b>Adresse:</b> {{ company.location || 'Unbekannt' }}</div>
                        </div>
                    </v-col>
                    <v-col cols="12" md="6">
                        <div class="section-card company-meta">
                            <div><b>Besitzer:</b> {{ company.owner?.name || 'Unbekannt' }}</div>
                            <div><b>Telefon:</b> {{ ownerPhone }}</div>
                            <div v-if="primaryRepresentative" class="mt-2"><b>Vertreter:</b> {{ primaryRepresentative.name || 'Unbekannt' }}</div>
                            <div v-if="primaryRepresentative && representativePhone !== 'Unbekannt'"><b>Vertreter Tel.:</b> {{ representativePhone }}</div>
                        </div>
                    </v-col>
                    <v-col cols="12">
                        <div class="section-card">
                            <div class="section-title"><b>Kostenschlüssel:</b></div>
                            <v-row class="mt-2" v-if="licenseItems.length > 0">
                            <v-col cols="12" md="8">
                                <div class="license-list">
                                    <div
                                        v-for="(license, index) in licenseItems"
                                        :key="`${license.name}-${index}`"
                                        class="license-item"
                                    >
                                        <div class="license-name">{{ license.name }}</div>
                                        <div class="license-meta">
                                            <div class="license-count-wrap">
                                                <span class="license-count">{{ license.count > 1 ? `${license.count}x` : '1x' }}</span>
                                                <small v-if="license.description" class="license-description">{{ license.description }}</small>
                                            </div>
                                            <span class="license-fee">{{ formatMoney(license.totalFeePerWeek) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </v-col>
                            <v-col cols="12" md="4" class="license-summary-col">
                                <div class="license-summary">
                                    <div class="license-summary-row">
                                        <span>Anfallende Kosten pro Woche</span>
                                        <strong>{{ formatMoney(licenseTotalPerWeek) }}</strong>
                                    </div>
                                    <div class="license-summary-row">
                                        <span>Anfallende Kosten pro 4 Wochen</span>
                                        <strong>{{ formatMoney(licenseTotalPerFourWeeks) }}</strong>
                                    </div>
                                    <div><b>Nächste Zahlung:</b> {{ formatDate(company.nextPaymentLicenses) }}</div>
                                </div>
                            </v-col>
                            </v-row>
                            <v-alert v-else dense outlined type="info" class="mt-2 mb-0">
                                Keine Lizenzen hinterlegt.
                            </v-alert>
                        </div>
                    </v-col>

                    <v-col cols="12" class="pt-2" v-if="nextInspection">
                        <div class="section-card">
                            <div class="section-title">
                                <v-icon small left :color="nextInspection.followUp ? 'orange' : 'teal'">
                                    {{ nextInspection.followUp ? 'mdi-alert-circle-outline' : 'mdi-clipboard-check-outline' }}
                                </v-icon>
                                <b>{{ nextInspection.followUp ? 'Nächste Nachkontrolle ab' : 'Nächste Kontrolle ab' }}:</b>
                                {{ formatDateOnly(nextInspection.date) }}
                            </div>
                            <div v-if="nextInspection.followUp && nextInspection.note" class="mt-2">
                                <v-icon x-small left color="grey lighten-1">mdi-note-text-outline</v-icon>
                                <span class="grey--text text--lighten-1">{{ nextInspection.note }}</span>
                            </div>
                        </div>
                    </v-col>

                    <v-col cols="12" class="pt-2">
                        <div class="section-card strike-section">
                            <div class="strike-section-title mb-2">
                                <v-icon small left color="#ffb74d">mdi-alert-outline</v-icon>
                                <b>Aktuelle Strikes:</b> {{ currentStrikePoints }} Punkte
                            </div>
                            <v-alert v-if="strikeDataTable.length === 0" dense outlined type="success">
                                Keine aktiven Strikes vorhanden.
                            </v-alert>

                            <v-data-table :items="strikeDataTable" sort-by="createdAt" sort-desc
                        :headers="[{text: 'Erstellt am', value: 'createdAt'}, {text: 'Läuft aus', value: 'expiresAfter'}, {text: 'Beschreibung', value: 'name'}, {text: 'Bußgeld', value: 'fine'}, {text: 'Punkte', value: 'points'}, {text: 'Aktionen', value: 'actions'}]">
                                <template v-slot:item.createdAt="{ item }">
                                <div v-if="item.revokedAt">
                                                    <span style="text-decoration-line: line-through">{{
                                                        visualizeDate(item.createdAt)
                                                    }}</span>
                                    ({{ visualizeDate(item.revokedAt) }})
                                </div>
                                <div v-else>
                                    {{ visualizeDate(item.createdAt) }}
                                </div>
                                </template>

                                <template v-slot:item.expiresAfter="{ item }">
                                {{ visualizeDate(item.expiresAfter) }}
                                </template>

                                <template v-slot:item.actions="{ item }">
                                <v-chip color="primary" small class="elevation-3 pr-2" v-if="isStrikeExpired(item)">Ausgelaufen</v-chip>
                                <v-chip color="warning" small class="elevation-3 pr-2" v-else-if="item.revoked">Zurückgezogen</v-chip>
                                <v-chip color="error" small class="elevation-3 pr-2" v-else>Aktiv</v-chip>
                                </template>
                            </v-data-table>
                        </div>
                    </v-col>
                </v-row>

                <v-alert v-else type="warning" dense outlined>
                    Für diesen Zugriffstoken wurden keine Unternehmensdaten gefunden.
                </v-alert>

            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
import moment from "moment";
import {COMPANY_TYPES} from "@/plugins/config";

export default {
    data: () => ({
        loading: true,
        company: null,
        error: null,
    }),
    computed: {
        accessToken() {
            return this.$route.params.accessToken;
        },
        companyTypeLabel() {
            const type = this.company?.type;
            return COMPANY_TYPES[type] || type || 'Unbekannt';
        },
        ownerPhone() {
            const owner = this.company?.owner;
            const candidates = [
                owner?.phoneNumber,
                owner?.phone,
                owner?.telephone,
                owner?.tel,
                owner?.phone_number,
            ];

            for (const candidate of candidates) {
                const normalized = `${candidate || ''}`.trim();
                if (normalized) return normalized;
            }

            return 'Unbekannt';
        },
        primaryRepresentative() {
            const lists = [
                this.company?.authorizedRepresentatives,
                this.company?.authorizedRepresentative,
                this.company?.representatives,
            ];

            for (const list of lists) {
                if (Array.isArray(list) && list.length > 0) {
                    return list[0];
                }
            }

            return null;
        },
        representativePhone() {
            const rep = this.primaryRepresentative;
            const candidates = [
                rep?.phoneNumber,
                rep?.phone,
                rep?.telephone,
                rep?.tel,
                rep?.phone_number,
            ];

            for (const candidate of candidates) {
                const normalized = `${candidate || ''}`.trim();
                if (normalized) return normalized;
            }

            return 'Unbekannt';
        },
        licenseItems() {
            const links = Array.isArray(this.company?.licenseLinks) ? this.company.licenseLinks : [];
            return links
                .map(link => {
                    const count = Math.max(1, Number(link?.count) || 1);
                    const fee = Number(link?.license?.fee ?? link?.fee ?? 0);

                    return {
                        name: link?.license?.name,
                        description: link?.license?.description || '',
                        count,
                        fee,
                        totalFeePerWeek: fee * count,
                    };
                })
                .filter(item => item.name);
        },
        licenseTotalPerFourWeeks() {
            const perWeek = this.licenseTotalPerWeek;
            const perDay = perWeek / 7;
            return perDay * 28;
        },
        licenseTotalPerWeek() {
            return this.licenseItems.reduce((sum, item) => sum + (Number(item.totalFeePerWeek) || 0), 0);
        },
        currentStrikes() {
            const strikes = Array.isArray(this.company?.strikes) ? this.company.strikes : [];
            const now = moment();

            return strikes.filter(strike => {
                if (!strike || strike.revoked) return false;
                if (!strike.expiresAfter) return true;
                return moment(strike.expiresAfter).isAfter(now);
            });
        },
        currentStrikePoints() {
            return this.currentStrikes.reduce((sum, strike) => sum + (Number(strike?.points) || 0), 0);
        },
        strikeHeaders() {
            return [
                {text: 'Erstellt am', value: 'createdAt'},
                {text: 'Läuft aus', value: 'expiresAfter'},
                {text: 'Beschreibung', value: 'name'},
                {text: 'Bußgeld', value: 'fine'},
                {text: 'Punkte', value: 'points'},
                {text: 'Ersteller', value: 'createdByName'},
                {text: 'Aktionen', value: 'actions', sortable: false},
            ];
        },
        strikeDataTable() {
            let result = [];

            const strikes = this.currentStrikes;
            for (const strike of strikes) {
                const creator = strike?.createdBy;
                const createdByName = typeof creator === 'string'
                    ? creator
                    : (creator?.displayName || creator?.name || 'Unbekannt');

                result.push({
                id: strike?.id,
                name: strike?.name || 'Unbekannt',
                fine: Number(strike?.fine) || 0,
                points: Number(strike?.points) || 0,
                createdByName,
                revokedByName: strike?.revokedBy?.displayName || strike?.revokedBy?.name || null,
                createdAt: strike?.createdAt ? moment(strike.createdAt).unix() : null,
                revokedAt: strike?.revokedAt ? moment(strike.revokedAt).unix() : null,
                expiresAfter: strike?.expiresAfter ? moment(strike.expiresAfter).unix() : null,
                revoked: strike?.revoked === null ? false : Boolean(strike?.revoked),
                payed: strike?.payed === null ? false : Boolean(strike?.payed),
                });
            }

            return result;
        },
        nextInspection() {
            const inspections = Array.isArray(this.company?.inspections) ? this.company.inspections : [];
            if (inspections.length === 0) return null;
            const sorted = [...inspections].sort((a, b) => {
                return new Date(b.createdAt) - new Date(a.createdAt);
            });
            const latest = sorted[0];
            if (!latest.nextInspectionAt) return null;
            return {
                date: latest.nextInspectionAt,
                followUp: Boolean(latest.followUp),
                note: latest.note || null,
            };
        },
    },
    methods: {
        toMoment(value) {
            if (value === null || value === undefined || value === '') return null;

            if (typeof value === 'number') {
                // Accept both unix seconds and unix milliseconds.
                return value > 9999999999 ? moment(value) : moment.unix(value);
            }

            return moment(value);
        },
        isStrikeExpired(strike) {
            const expiresAt = this.toMoment(strike?.expiresAfter);
            if (!expiresAt || !expiresAt.isValid()) return false;
            return moment().isAfter(expiresAt);
        },
        visualizeDate(value) {
            const parsed = this.toMoment(value);
            return parsed && parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : 'N/A';
        },
        deepSearchPreferredText(source, preferredKeys = [], visited = new Set()) {
            if (source === null || source === undefined) return '';
            if (typeof source === 'string') {
                const normalized = source.trim();
                return normalized || '';
            }
            if (typeof source === 'number') return `${source}`;
            if (typeof source !== 'object') return '';
            if (visited.has(source)) return '';
            visited.add(source);

            if (Array.isArray(source)) {
                for (const item of source) {
                    const candidate = this.deepSearchPreferredText(item, preferredKeys, visited);
                    if (candidate) return candidate;
                }
                return '';
            }

            for (const key of preferredKeys) {
                if (!(key in source)) continue;
                const candidate = this.deepSearchPreferredText(source[key], preferredKeys, visited);
                if (candidate) return candidate;
            }

            for (const [key, value] of Object.entries(source)) {
                if (preferredKeys.includes(key)) continue;
                const candidate = this.deepSearchPreferredText(value, preferredKeys, visited);
                if (candidate) return candidate;
            }

            return '';
        },
        pickFirstFilled(...values) {
            for (const value of values) {
                if (value === null || value === undefined) continue;

                if (typeof value === 'string') {
                    const normalized = value.trim();
                    if (normalized) return normalized;
                    continue;
                }

                if (typeof value === 'number') {
                    return `${value}`;
                }

                if (typeof value === 'object') {
                    const textCandidate = this.pickFirstFilled(
                        value.name,
                        value.label,
                        value.title,
                        value.description,
                        value.content,
                        value.reason,
                        value.text,
                    );
                    if (textCandidate) return textCandidate;
                }
            }

            return '';
        },
        normalizeCompanyForPublicView(company) {
            if (!company || typeof company !== 'object') return company;

            const normalized = {...company};
            const strikes = Array.isArray(normalized.strikes) ? normalized.strikes : [];

            normalized.strikes = strikes.map(strike => {
                if (!strike || typeof strike !== 'object') return strike;

                const resolvedName = this.pickFirstFilled(
                    strike.name,
                    strike.title,
                    strike.label,
                    strike.description,
                    strike.content,
                    strike.reason,
                    strike.text,
                    strike.type,
                    strike.strikeType,
                    strike.strikeReason,
                    strike.strike,
                    strike.rule,
                    strike.violation,
                    strike.template,
                    strike.meta,
                    this.deepSearchPreferredText(strike, [
                        'name',
                        'title',
                        'label',
                        'description',
                        'content',
                        'reason',
                        'text',
                        'type',
                        'strikeType',
                        'strikeReason',
                        'strike',
                        'rule',
                        'violation',
                    ]),
                ) || 'Unbekannt';

                return {
                    ...strike,
                    name: resolvedName,
                };
            });

            if (process.env.NODE_ENV !== 'production') {
                console.debug('[BusinessInfo] raw strikes', strikes);
                console.debug('[BusinessInfo] normalized strikes', normalized.strikes.map((strike, index) => ({
                    id: strike?.id,
                    name: strike?.name,
                    raw: strikes[index],
                })));
            }

            return normalized;
        },
        formatDate(value) {
            if (!value) return 'Unbekannt';
            const parsed = this.toMoment(value);
            return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : 'Unbekannt';
        },
        formatDateOnly(value) {
            if (!value) return 'Unbekannt';
            const parsed = this.toMoment(value);
            return parsed && parsed.isValid() ? parsed.format('DD.MM.YYYY') : 'Unbekannt';
        },
        async fetchCompanyByAccessToken() {
            const token = `${this.accessToken || ''}`.trim();

            this.loading = true;
            this.company = null;
            this.error = null;

            if (!token) {
                this.error = 'Es wurde kein Zugriffstoken angegeben.';
                this.loading = false;
                return;
            }

            try {
                const response = await this.apiGetCompanyByAccessToken(token);
                this.company = this.normalizeCompanyForPublicView(response?.data || null);
            } catch (err) {
                this.error = err?.response?.data?.message || err?.message || 'Unternehmensdaten konnten nicht geladen werden.';
            }
            this.loading = false;
        },
    },
    watch: {
        accessToken() {
            this.fetchCompanyByAccessToken();
        },
    },
    mounted() {
        this.fetchCompanyByAccessToken();
    },
}
</script>

<style scoped>
.company-meta {
    font-size: 1.03rem;
    line-height: 1.45;
}

.section-card {
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.04);
}

.section-title {
    font-size: 1.02rem;
    margin-bottom: 4px;
}

.license-list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
}

.license-item {
    border: 1px solid rgba(255, 255, 255, 0.24);
    border-radius: 8px;
    padding: 8px 10px;
}

.license-name {
    font-weight: 600;
    line-height: 1.3;
    word-break: break-word;
}

.license-meta {
    margin-top: 4px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    font-size: 0.85rem;
    opacity: 0.9;
}

.license-count {
    white-space: nowrap;
}

.license-count-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
}

.license-description {
    margin-top: 2px;
    font-size: 0.72rem;
    line-height: 1.2;
    opacity: 0.75;
    white-space: normal;
    word-break: break-word;
}

.license-fee {
    white-space: nowrap;
    font-weight: 600;
}

.license-summary {
    border: 1px solid rgba(255, 255, 255, 0.24);
    border-radius: 8px;
    padding: 10px;
    display: grid;
    gap: 8px;
}

.license-summary-col {
    align-self: flex-start;
}

.license-summary-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.strike-section-title {
    font-size: 1.02rem;
}

.strike-section {
    border-color: rgba(255, 183, 77, 0.35);
    background: rgba(255, 183, 77, 0.06);
}
</style>